import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Check, X as XIcon, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ConfirmDialog";

const STATUS_PILL = {
  submitted: "bg-navy/10 text-navy",
  pending_payment: "bg-gold/15 text-gold-deep",
  pending_review: "bg-gold text-navy",
  accepted: "bg-emerald-100 text-emerald-800",
  declined: "bg-navy/5 text-navy/60",
  refunded: "bg-navy/5 text-navy/60",
};

function PageHeader({ overline, title }) {
  return (
    <div className="border-b border-navy/10 px-8 py-8 bg-cream">
      <div className="overline text-gold-deep">{overline}</div>
      <h1 className="font-serif text-3xl md:text-4xl text-navy mt-2">{title}</h1>
      <div className="gold-rule mt-4" />
    </div>
  );
}

export default function AdminIntros() {
  const [intros, setIntros] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/intros");
    setIntros(data);
  };
  useEffect(() => { load(); }, []);

  const opened = intros.find((i) => i.id === openId) || null;

  const decide = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/intros/${openId}/${action}`, { notes });
      if (action === "accept") {
        if (data?.auto_deal_id) {
          toast.success("Accepted — Contact + Deal auto-created in your pipeline", { duration: 6000 });
        } else {
          toast.success("Marked accepted");
        }
      } else {
        toast.success("Declined" + (opened.payment_status === "paid" ? " & refunded" : ""));
      }
      setOpenId(null);
      setNotes("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, e) => {
    e?.stopPropagation();
    if (!(await confirmDialog({ title: "Delete this consultation request?", description: "This permanently removes the request from your queue. Use Decline (with refund) if it has been paid for.", confirmLabel: "Delete", destructive: true }))) return;
    await api.delete(`/intros/${id}`);
    await load();
  };

  const pending = intros.filter((i) => i.status === "pending_review");
  const decided = intros.filter((i) => !["pending_review", "submitted"].includes(i.status));

  return (
    <div data-testid="admin-intros-page">
      <PageHeader overline="Expert consultation" title="Consultation requests" />
      <div className="p-8 space-y-10">
        <section>
          <h2 className="overline text-gold-deep mb-3">Awaiting your decision ({pending.length})</h2>
          {pending.length === 0 && <div className="text-sm italic text-navy/40">No pending intros.</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pending.map((i) => (
              <button
                key={i.id}
                onClick={() => { setOpenId(i.id); setNotes(""); }}
                data-testid={`intro-card-${i.id}`}
                className="text-left bg-white border border-navy/10 hover:border-gold p-5 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium text-navy">{i.requester_name}</div>
                    <div className="text-xs text-navy/60">{i.requester_org} · {i.requester_role}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${STATUS_PILL[i.status]}`}>
                    {i.status.replace("_", " ")}
                  </span>
                </div>
                <div className="mt-3 text-sm text-navy/80 line-clamp-2">{i.target_description}</div>
                <div className="mt-3 flex items-center gap-3 text-xs text-navy/50">
                  <span className="font-medium text-gold-deep">{i.tier_label}</span>
                  <span>·</span>
                  <span>${(i.amount_usd || 0).toFixed(0)} USD</span>
                  {i.region && <><span>·</span><span>{i.region}</span></>}
                  {i.sector && <><span>·</span><span>{i.sector}</span></>}
                </div>
              </button>
            ))}
          </div>
        </section>

        <section>
          <h2 className="overline text-gold-deep mb-3">History ({decided.length})</h2>
          {decided.length === 0 && <div className="text-sm italic text-navy/40">Nothing yet.</div>}
          <div className="bg-white border border-navy/10">
            {decided.map((i) => (
              <div
                key={i.id}
                onClick={() => { setOpenId(i.id); setNotes(i.decision_notes || ""); }}
                data-testid={`intro-history-${i.id}`}
                className="border-b border-navy/5 last:border-b-0 p-4 flex items-center justify-between gap-4 hover:bg-cream/50 cursor-pointer"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-navy truncate">{i.requester_name} — {i.requester_org || "—"}</div>
                  <div className="text-xs text-navy/50 truncate">{i.target_description}</div>
                  {i.auto_deal_id && (
                    <a
                      href="/dashboard/deals"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`intro-deal-link-${i.id}`}
                      className="inline-block mt-1 text-[10px] uppercase tracking-widest text-gold-deep hover:underline"
                    >
                      → Auto-deal in pipeline
                    </a>
                  )}
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${STATUS_PILL[i.status]}`}>
                  {i.status.replace("_", " ")}
                </span>
                <span className="text-xs text-navy/40 w-20 text-right">${(i.amount_usd || 0).toFixed(0)}</span>
                <button onClick={(e) => remove(i.id, e)} data-testid={`intro-delete-${i.id}`} className="text-navy/40 hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>

      {opened && (
        <div className="fixed inset-0 z-50 bg-navy/40 flex items-center justify-center p-4" data-testid="intro-modal">
          <div className="bg-white max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline text-gold-deep">{opened.tier_label} · ${(opened.amount_usd || 0).toFixed(0)} USD</div>
                <h2 className="font-serif text-2xl text-navy mt-1">{opened.requester_name}</h2>
                <div className="text-sm text-navy/60">{opened.requester_org} · {opened.requester_role}</div>
                <div className="text-sm text-navy/60">{opened.requester_email}</div>
              </div>
              <button onClick={() => setOpenId(null)} className="text-navy/40 hover:text-navy">
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="gold-rule mb-5" />

            <div className="space-y-4">
              <div>
                <div className="overline text-navy/60">Target</div>
                <p className="mt-1 text-navy/85 whitespace-pre-wrap">{opened.target_description}</p>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div><div className="overline text-navy/60">Region</div><div className="text-navy/80">{opened.region || "—"}</div></div>
                <div><div className="overline text-navy/60">Sector</div><div className="text-navy/80">{opened.sector || "—"}</div></div>
                <div><div className="overline text-navy/60">Stakeholder</div><div className="text-navy/80">{opened.stakeholder_type || "—"}</div></div>
              </div>
              <div className="text-xs text-navy/50">
                Submitted {(opened.created_at || "").slice(0, 16).replace("T", " ")}
                {opened.payment_status === "paid" && <span className="ml-2 text-gold-deep">• PAID</span>}
                {opened.stripe_refund_id && <span className="ml-2 text-navy/60">• Refunded ({opened.stripe_refund_id})</span>}
              </div>

              {(opened.status === "pending_review") && (
                <div>
                  <label className="overline text-navy/60">Notes (private)</label>
                  <textarea
                    rows={3}
                    data-testid="intro-decision-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full mt-1.5 border border-navy/15 bg-transparent p-3 focus:outline-none focus:border-gold resize-none text-sm"
                  />
                </div>
              )}
              {opened.decision_notes && opened.status !== "pending_review" && (
                <div>
                  <div className="overline text-navy/60">Decision notes</div>
                  <p className="mt-1 text-sm text-navy/70 whitespace-pre-wrap">{opened.decision_notes}</p>
                </div>
              )}
            </div>

            {opened.status === "pending_review" && (
              <div className="mt-8 flex flex-wrap justify-end gap-3">
                <button
                  onClick={() => decide("decline")}
                  disabled={busy}
                  data-testid="intro-decline"
                  className="border border-navy text-navy px-6 py-2.5 text-xs uppercase tracking-widest hover:bg-navy hover:text-gold flex items-center gap-2 disabled:opacity-50"
                >
                  {opened.payment_status === "paid" ? <RefreshCw className="h-3.5 w-3.5" /> : <XIcon className="h-3.5 w-3.5" />}
                  {opened.payment_status === "paid" ? "Decline & refund" : "Decline"}
                </button>
                <button
                  onClick={() => decide("accept")}
                  disabled={busy}
                  data-testid="intro-accept"
                  className="bg-navy text-gold px-6 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600 flex items-center gap-2 disabled:opacity-50"
                >
                  <Check className="h-3.5 w-3.5" /> Accept intro
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
